import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CreateBetaRegistrationDto, FrenchDepartment, PropertySaleUrgency } from '@workspace/data-types';
import { PhoneNumberInputComponent } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

export interface BetaJoinDialogData {
  role?: 'vendeur' | 'agent';
  tokenVaultId?: string;
  token?: string;
  onlyCountries?: string[];
  preferredCountries?: string[];
}

export interface DepartmentOption {
  code: string;
  label: string;
}

export interface SaleUrgencyOption {
  value: PropertySaleUrgency;
  label: string;
}

const FRENCH_DEPARTMENT_NAMES: Record<string, string> = {
  '01': 'Ain',
  '02': 'Aisne',
  '03': 'Allier',
  '04': 'Alpes-de-Haute-Provence',
  '05': 'Hautes-Alpes',
  '06': 'Alpes-Maritimes',
  '07': 'Ardèche',
  '08': 'Ardennes',
  '09': 'Ariège',
  '10': 'Aube',
  '11': 'Aude',
  '12': 'Aveyron',
  '13': 'Bouches-du-Rhône',
  '14': 'Calvados',
  '15': 'Cantal',
  '16': 'Charente',
  '17': 'Charente-Maritime',
  '18': 'Cher',
  '19': 'Corrèze',
  '21': "Côte-d'Or",
  '22': "Côtes-d'Armor",
  '23': 'Creuse',
  '24': 'Dordogne',
  '25': 'Doubs',
  '26': 'Drôme',
  '27': 'Eure',
  '28': 'Eure-et-Loir',
  '29': 'Finistère',
  '2A': 'Corse-du-Sud',
  '2B': 'Haute-Corse',
  '30': 'Gard',
  '31': 'Haute-Garonne',
  '32': 'Gers',
  '33': 'Gironde',
  '34': 'Hérault',
  '35': 'Ille-et-Vilaine',
  '36': 'Indre',
  '37': 'Indre-et-Loire',
  '38': 'Isère',
  '39': 'Jura',
  '40': 'Landes',
  '41': 'Loir-et-Cher',
  '42': 'Loire',
  '43': 'Haute-Loire',
  '44': 'Loire-Atlantique',
  '45': 'Loiret',
  '46': 'Lot',
  '47': 'Lot-et-Garonne',
  '48': 'Lozère',
  '49': 'Maine-et-Loire',
  '50': 'Manche',
  '51': 'Marne',
  '52': 'Haute-Marne',
  '53': 'Mayenne',
  '54': 'Meurthe-et-Moselle',
  '55': 'Meuse',
  '56': 'Morbihan',
  '57': 'Moselle',
  '58': 'Nièvre',
  '59': 'Nord',
  '60': 'Oise',
  '61': 'Orne',
  '62': 'Pas-de-Calais',
  '63': 'Puy-de-Dôme',
  '64': 'Pyrénées-Atlantiques',
  '65': 'Hautes-Pyrénées',
  '66': 'Pyrénées-Orientales',
  '67': 'Bas-Rhin',
  '68': 'Haut-Rhin',
  '69': 'Rhône',
  '70': 'Haute-Saône',
  '71': 'Saône-et-Loire',
  '72': 'Sarthe',
  '73': 'Savoie',
  '74': 'Haute-SAVOIE',
  '75': 'Paris',
  '76': 'Seine-Maritime',
  '77': 'Seine-et-Marne',
  '78': 'Yvelines',
  '79': 'Deux-Sèvres',
  '80': 'Somme',
  '81': 'Tarn',
  '82': 'Tarn-et-Garonne',
  '83': 'Var',
  '84': 'Vaucluse',
  '85': 'Vendée',
  '86': 'Vienne',
  '87': 'Haute-Vienne',
  '88': 'Vosges',
  '89': 'Yonne',
  '90': 'Territoire de Belfort',
  '91': 'Essonne',
  '92': 'Hauts-de-Seine',
  '93': 'Seine-Saint-Denis',
  '94': 'Val-de-Marne',
  '95': "Val-d'Oise",
  '971': 'Guadeloupe',
  '972': 'Martinique',
  '973': 'Guyane',
  '974': 'La Réunion',
  '976': 'Mayotte',
};

@Component({
  selector: 'etxe-beta-join-dialog',
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    PhoneNumberInputComponent,
  ],
  templateUrl: './beta-join-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BetaJoinDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<BetaJoinDialogComponent>);
  private readonly data = inject<BetaJoinDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly api = inject(ApiService);

  // Constants & Dropdown Options
  readonly frenchDepartments: DepartmentOption[] = Object.values(FrenchDepartment)
    .map((code) => ({
      code,
      label: `${code} - ${FRENCH_DEPARTMENT_NAMES[code] || code}`,
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

  readonly saleUrgencyOptions: SaleUrgencyOption[] = [
    { value: PropertySaleUrgency.AS_SOON_AS_POSSIBLE, label: 'Au plus vite' },
    { value: PropertySaleUrgency.ONE_TO_THREE_MONTHS, label: 'Dans 1 à 3 mois' },
    { value: PropertySaleUrgency.THREE_TO_SIX_MONTHS, label: 'Dans 3 à 6 mois' },
    { value: PropertySaleUrgency.MORE_THAN_SIX_MONTHS, label: 'Dans plus de 6 mois' },
  ];

  readonly preferredCountries = signal<string[]>(this.data?.preferredCountries ?? ['fr']);
  readonly onlyCountries = signal<string[]>(this.data?.onlyCountries ?? ['fr', 'es', 'be', 'ch', 'mc']);

  // Reactive Form
  readonly formGroup = new FormGroup({
    role: new FormControl<'vendeur' | 'agent'>(this.data?.role ?? 'vendeur', { nonNullable: true }),
    firstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    lastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    mobileNumber: new FormControl<string | null>(null),
    agentDepartments: new FormControl<string[]>([], { nonNullable: true }),
    agentCarteT: new FormControl<string>(''),
    sellerSaleUrgency: new FormControl<string>(''),
    sellerDepartment: new FormControl<string>(''),
  });

  readonly isBetaSubmitted = signal<boolean>(false);
  readonly isBetaSubmitting = signal<boolean>(false);
  readonly betaErrorMessage = signal<string | null>(null);

  // Token Vault Email Verification State
  readonly returnedTokenVaultId = signal<string | null>(this.data?.tokenVaultId ?? null);
  readonly verificationTokenInput = signal<string>('');
  readonly isVerifyingCode = signal<boolean>(false);
  readonly isEmailVerified = signal<boolean>(false);
  readonly verificationFeedbackMessage = signal<string | null>(null);

  constructor() {
    if (this.data?.tokenVaultId && this.data?.token) {
      this.isBetaSubmitted.set(true);
      this.verifyTokenVault(this.data.tokenVaultId, this.data.token);
    }
  }

  setBetaRole(role: 'vendeur' | 'agent'): void {
    this.formGroup.controls.role.setValue(role);
  }

  formatAndSetCarteT(): void {
    const current = this.formGroup.controls.agentCarteT.value;
    if (!current) return;
    const formatted = this.formatCarteT(current);
    this.formGroup.controls.agentCarteT.setValue(formatted, { emitEvent: false });
  }

  formatCarteT(input: string): string {
    if (!input) return '';
    const clean = input.trim().toUpperCase().replace(/\s/g, '');
    const withoutPrefix = clean.startsWith('CPI') ? clean.slice(3) : clean;
    const digitsOnly = withoutPrefix.replace(/\D/g, '');
    if (!digitsOnly) return input.trim();
    const padded = digitsOnly.padStart(17, '0').slice(-17);
    return `CPI${padded}`;
  }

  updateVerificationTokenInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.verificationTokenInput.set(input.value);
  }

  submitBetaForm(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isBetaSubmitting.set(true);
    this.betaErrorMessage.set(null);

    const values = this.formGroup.getRawValue();
    const mobileNumber = values.mobileNumber?.trim() || undefined;
    const metadata: Record<string, unknown> = {};

    if (values.role === 'agent') {
      if (values.agentDepartments && values.agentDepartments.length > 0) {
        metadata['departments'] = values.agentDepartments;
      }
      if (values.agentCarteT) {
        metadata['carteTNumber'] = this.formatCarteT(values.agentCarteT);
      }
    } else if (values.role === 'vendeur') {
      if (values.sellerSaleUrgency) {
        metadata['saleUrgency'] = values.sellerSaleUrgency;
      }
      if (values.sellerDepartment) {
        metadata['department'] = values.sellerDepartment;
      }
    }

    const payload: CreateBetaRegistrationDto = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      role: values.role,
      ...(mobileNumber ? { mobileNumber } : {}),
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    };

    this.api
      .business()
      .etxe()
      .public()
      .betaRegistration()
      .register(payload)
      .pipe(
        tap((res) => {
          this.isBetaSubmitted.set(true);
          if (res?.tokenVaultId) {
            this.returnedTokenVaultId.set(res.tokenVaultId);
          }
        }),
        catchError((err) => {
          this.betaErrorMessage.set(
            err?.error?.message || 'Une erreur est survenue lors de la demande. Veuillez réessayer.',
          );
          return EMPTY;
        }),
        finalize(() => this.isBetaSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  verifyTokenVault(tokenVaultId: string, token: string): void {
    this.isVerifyingCode.set(true);
    this.verificationFeedbackMessage.set(null);

    this.api
      .business()
      .etxe()
      .public()
      .betaRegistration()
      .verify({ tokenVaultId, token })
      .pipe(
        tap((res) => {
          this.isEmailVerified.set(true);
          this.verificationFeedbackMessage.set(res.message || 'Adresse email validée avec succès !');
        }),
        catchError((err) => {
          this.verificationFeedbackMessage.set(
            err?.error?.message || 'Erreur lors de la validation du code de vérification',
          );
          return EMPTY;
        }),
        finalize(() => this.isVerifyingCode.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  submitVerificationCode(): void {
    const tokenVaultId = this.returnedTokenVaultId();
    const token = this.verificationTokenInput().trim();
    if (tokenVaultId && token) {
      this.verifyTokenVault(tokenVaultId, token);
    }
  }

  closeBetaModal(): void {
    this.dialogRef.close();
  }
}
